import { ChangeEvent, FunctionComponent, HTMLProps, RefObject } from 'react';
import styled from 'styled-components';

import InputLabel, { InputLabelProps } from './InputLabel';

export interface TextareaProps
  extends HTMLProps<HTMLTextAreaElement>,
    Pick<InputLabelProps, 'labelSpacing'> {
  className?: string;
  height?: string;
  value: string;
  textareaRef?: RefObject<HTMLTextAreaElement>;
}

const TextareaMobile: FunctionComponent<TextareaProps> = ({
  className,
  id,
  label,
  labelSpacing = '12px',
  placeholder,
  disabled = false,
  maxLength = 0,
  height = '128px',
  value,
  onChange,
  textareaRef,
}) => {
  const onCheckMaxLength = (object: ChangeEvent<HTMLTextAreaElement>) => {
    if (object.target.value.length > object.target.maxLength) {
      object.target.value = object.target.value.slice(
        0,
        object.target.maxLength,
      );
    }
  };
  return (
    <div className={className}>
      <LabelWrapper>
        {label && (
          <InputLabel htmlFor={id} label={label} labelSpacing={labelSpacing} />
        )}
        {maxLength > 0 && (
          <TextareaLength labelSpacing={labelSpacing}>
            <b>{value.length}</b> / {maxLength}
          </TextareaLength>
        )}
      </LabelWrapper>
      <TextareaWrapper>
        <TextareaStyled
          id={id}
          ref={textareaRef}
          height={height}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          value={value}
          onChange={onChange}
          onInput={onCheckMaxLength}
        />
      </TextareaWrapper>
    </div>
  );
};

export default TextareaMobile;

export const TextareaWrapper = styled.div`
  position: relative;
  border-radius: 5px;
  display: flex;
  flex-direction: column;

  ${({ theme }) => `
    border: 1px solid ${theme.color.gray200};

    &:focus-within {
      border-color:${theme.color.gray400};
    }
  `};
`;

export const TextareaStyled = styled.textarea<{
  height: string;
  maxLength: number;
}>`
  width: 100%;
  box-sizing: border-box;
  border: none;
  padding: 16px;
  border-radius: 8px;

  ${({ theme, height }) => `
    ${theme.typography.ARTICLE16R}
    color: ${theme.color.gray700};
    height: ${height};
  
    &::placeholder {
      ${theme.typography.UI16R};
      color: ${theme.color.gray200};
    }
  
    &:focus {
      border-color: ${theme.color.gray400};
    }
    
    &:disabled {
      color: ${theme.color.gray100};
      background-color: ${theme.color.gray20};
    }
    `};
`;

export const LabelWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const TextareaLength = styled.div<{ labelSpacing?: string }>`
  ${({ theme, labelSpacing }) => `
    margin-bottom: ${labelSpacing};

    ${theme.typography.UI14M};
    color: ${theme.color.gray400};
    
    b {
      ${theme.typography.UI14SB};
      color: ${theme.color.gray600};
    }
    `};
`;
